// `common/components/gif-player/gif-player.tpl.html` + `super-gif.directive.ng1.ts`, ported.
//
// Two screens of the enable-cookies flow are almost entirely this component, and the first
// port stood in for it with a bare `<img>` under invented class names — which left
// `/auth/enable-cookies` the worst route in the pixel diff by a wide margin (11.8–17.2%,
// against 1.8–7.0% for login). The markup and the class names were most of that:
// `gif-title` not `gif-player-title`, plus a `.gif-animation-wrapper` the stylesheet lays
// out and an element host that changes the box model around it.
//
// ⚠️ AND THE LAST 4.40% WAS THE LIBRARY, NOT A STYLE. Upstream hands the `<img>` to SuperGif
// (`libgif`, pinned at 0.0.3 in `careero/package.json`), which parses the GIF, REPLACES the
// img with `div.jsgif > canvas`, and sizes that canvas from the GIF's own header. Measured
// against production at 378: every box down to here agreed to the pixel, and then
// `div.gif-animation-wrapper` was 332 there and 330 here — the canvas takes the GIF's
// intrinsic height where an `<img>` takes the laid-out one. The page is centred, so 2px of
// content became a 1px offset of EVERYTHING above and below, and an otherwise identical
// screen scored 4.40%. The library is the fix; pinning a height that happens to match at one
// width is not.
//
// ⚠️ IT ALSO MAKES THE SCREEN DETERMINISTIC, which matters more than the two pixels. An
// `<img>` of a GIF animates, so a capture photographs whichever frame the browser had
// reached — `auto_play` defaults to FALSE here as it does upstream, and SuperGif then draws
// frame 1 and waits. A reference taken of a moving image is a reference that cannot be
// reproduced.
//
// ── REACT AND A LIBRARY THAT DELETES YOUR DOM ────────────────────────────────────────────
// `init()` does `parent.insertBefore(div, gif); parent.removeChild(gif)`. A node React
// rendered and no longer owns is how you get "The node to be removed is not a child of this
// node" on unmount, so the img is created imperatively and `.gif-animation-wrapper` has NO
// React children. The controls go back in through a PORTAL, which is the supported way to
// put React-managed nodes inside foreign DOM — and it lands them after `div.jsgif`, which is
// the order upstream's template has.
// ⚠️ The reference is load-bearing, not tidy-up. `libgif.d.ts` is an ambient declaration,
// and an ambient file is only in a program if that program's `include` globs reach it —
// which `packages/ui`'s do and `apps/spa`'s and `apps/shell`'s do not: they reach this
// FILE, through an import, and nothing else in the directory. Without the reference the two
// apps fail with "Could not find a declaration file for module 'libgif'" while the package
// that owns it compiles cleanly, which reads as a dependency problem and is not one.
/// <reference path="./libgif.d.ts" />
import SuperGif, { type SuperGifInstance } from 'libgif'
import { createElement, useEffect, useRef, useState, type ReactElement } from 'react'
import { createPortal } from 'react-dom'

export interface GifPlayerProps {
  title: string
  gifSrc: string
  /** Upstream's defaults, binding for binding (`gif-player.component.ng1.ts`). */
  autoPlay?: boolean
  showProgressBar?: boolean
  progressBarHeight?: number
  progressBarBackgroundColor?: string
  progressBarForegroundColor?: string
}

export function GifPlayer({
  title,
  gifSrc,
  autoPlay = false,
  showProgressBar = true,
  progressBarHeight = 3,
  progressBarBackgroundColor = 'rgba(0,0,0,0)',
  progressBarForegroundColor = 'rgb(0,52,89)',
}: GifPlayerProps): ReactElement {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [player, setPlayer] = useState<SuperGifInstance | undefined>(undefined)
  const [playing, setPlaying] = useState(autoPlay)

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (wrapper === null) return undefined

    const image = document.createElement('img')
    image.className = 'gif-animation'
    image.alt = title
    image.src = gifSrc
    wrapper.append(image)

    let live = true
    const instance = new SuperGif({
      gif: image,
      auto_play: autoPlay,
      show_progress_bar: showProgressBar,
      progressbar_height: progressBarHeight,
      progressbar_background_color: progressBarBackgroundColor,
      progressbar_foreground_color: progressBarForegroundColor,
    })
    // The directive calls `load` on `$observe('src')` — i.e. as soon as there is a src,
    // not on the img's load event. Same here: the library does its own fetching.
    instance.load(() => {
      if (live) setPlayer(instance)
    })

    return () => {
      live = false
      // Whatever is in there now — the img if the parse never finished, `div.jsgif` if it
      // did. Both are ours and neither is React's.
      for (const node of Array.from(wrapper.children)) {
        if (node instanceof HTMLImageElement || node.classList.contains('jsgif')) node.remove()
      }
    }
  }, [gifSrc, title, autoPlay, showProgressBar, progressBarHeight, progressBarBackgroundColor, progressBarForegroundColor])

  // The element wraps the classed div, as every AngularJS component does — unstyled, so it
  // is an inline box, and leaving it out changes the layout of what is inside it.
  return createElement(
    'gif-player',
    null,
    <div className="gif-player">
      <div className="gif-title">{title}</div>
      <div className="gif-animation-wrapper" ref={wrapperRef}>
        {/* `ng-if="!!ctrl.superGif"`: the controls appear only once the library has called
            back, because before that there is nothing to play or pause. */}
        {wrapperRef.current !== null && player !== undefined
          ? createPortal(
              <div className="gif-controls">
                <i
                  className={`fas ${playing ? 'fa-pause' : 'fa-play'}`}
                  onClick={() => {
                    if (playing) player.pause()
                    else player.play()
                    // ⚠️ NOT `player.get_playing()`, which upstream comments out with
                    // "returns `true` on load when it's actually false :(". The toggle is
                    // the source of truth; asking the library reintroduces the bug its own
                    // author left a note about.
                    setPlaying(!playing)
                  }}
                />
              </div>,
              wrapperRef.current,
            )
          : null}
      </div>
    </div>,
  )
}
